import logger from 'ws-scripts/modules/logger';
import { getCachedCoordinates, setCachedCoordinates } from './lruCache';
import { waitForGoogleMaps, geocodeSingleAddress } from './geocoder';
import { buildAddressString } from './addressBuilder';

const LOG_PREFIX = '[ws-locations-search][clientGeocoding]';

/**
 * Add coordinates to a single location using client-side geocoding
 * @param {Object} location - Location object
 * @returns {Promise<Object>} - Location object with coordinates added
 */
export const addCoordinatesToLocation = async (location) => {
	if (location.latitude && location.longitude) {
		return location;
	}

	const addressString = buildAddressString(location);
	if (!addressString.trim()) {
		logger.warn(
			`${LOG_PREFIX} No address for location: ${
				location.name || location.id
			}`
		);
		return location;
	}

	// Check localStorage cache first
	const cached = getCachedCoordinates(addressString);
	if (cached) {
		return {
			...location,
			latitude: cached.latitude,
			longitude: cached.longitude
		};
	}

	const isGoogleMapsReady = await waitForGoogleMaps();
	if (!isGoogleMapsReady) {
		return location;
	}

	try {
		const coordinates = await geocodeSingleAddress(addressString);
		if (coordinates) {
			setCachedCoordinates(addressString, coordinates);
			return {
				...location,
				latitude: coordinates.latitude,
				longitude: coordinates.longitude
			};
		}
	} catch (error) {
		logger.error(`${LOG_PREFIX} Error geocoding location:`, error);
	}

	return location;
};

/**
 * Simple async delay helper
 * @param {number} ms - Milliseconds to wait
 * @returns {Promise<void>}
 */
const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Add coordinates to multiple locations using client-side geocoding with rate limiting.
 * Locations already in the cache are served instantly; only cache-misses hit the
 * Google Maps API (guarded by waitForGoogleMaps inside addCoordinatesToLocation).
 * @param {Array} locations - Array of location objects
 * @param {number} delayMs - Delay between API requests in milliseconds (default: 50ms)
 * @returns {Promise<Array>} - Array of locations with coordinates added
 */
export const addCoordinatesToLocations = async (locations, delayMs = 50) => {
	if (!Array.isArray(locations) || locations.length === 0) {
		return [];
	}

	const enrichedLocations = [];

	for (let i = 0; i < locations.length; i++) {
		try {
			// eslint-disable-next-line no-await-in-loop
			const result = await addCoordinatesToLocation(locations[i]);
			enrichedLocations.push(result);
		} catch (error) {
			logger.error(
				`${LOG_PREFIX} Error processing location ${i} (${
					locations[i].name || 'Unknown'
				}):`,
				error
			);
			enrichedLocations.push(locations[i]);
		}

		// Rate-limit between API requests (skip after last item)
		if (i < locations.length - 1 && delayMs > 0) {
			// eslint-disable-next-line no-await-in-loop
			await delay(delayMs);
		}
	}

	return enrichedLocations;
};
